import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCheckCircle,
  faCloudUploadAlt,
  faDownload,
  faExclamationTriangle,
  faEye,
  faFileAlt,
  faHistory,
  faInfoCircle,
  faTimes,
  faTimesCircle,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { uploadCptBatch } from "../../services/cptBatchUploadApi";

const CSV_TEMPLATE_HEADERS = ["cpt_code", "cpt_desc", "default_price"];

const CSV_COLUMNS = [
  {
    label: "cpt_code",
    required: true,
    note: "Any characters, must be unique against existing CPT codes.",
  },
  {
    label: "cpt_desc",
    required: true,
    note: "CPT description, up to 200 characters.",
  },
  {
    label: "default_price",
    required: true,
    note: "Decimal amount with up to 2 decimal places.",
  },
];

const CptBatchUploadPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    const isValidType =
      selectedFile.type === "text/csv" ||
      selectedFile.type === "text/plain" ||
      selectedFile.name.toLowerCase().endsWith(".csv");

    if (!isValidType) {
      toast.error("Please select a valid CSV file (.csv).");
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("File size exceeds 10 MB limit.");
      return;
    }

    setFile(selectedFile);
    setUploadResult(null);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file first.");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await uploadCptBatch(formData);
      const result = response.data || {};

      setUploadResult({
        success: true,
        uploadId: result.upload_id,
        fileName: result.file_name,
        fileSize: result.file_size,
        message: result.message,
      });

      toast.success("File uploaded successfully. Processing started.");
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      const errMsg = error.response?.data?.message || error.message || "Upload failed.";
      setUploadResult({ success: false, message: errMsg });
      toast.error(typeof errMsg === "string" ? errMsg : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    const droppedFiles = event.dataTransfer.files;
    if (droppedFiles.length > 0) {
      handleFileSelect({ target: { files: droppedFiles } });
    }
  };

  const handleDownloadTemplate = () => {
    const csvContent = `${CSV_TEMPLATE_HEADERS.join(",")}\n`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "cpt_batch_upload_template.csv");
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Template downloaded.");
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-1">CPT Batch Upload</h1>
          <p className="text-gray-600">Upload CPT master records using the legacy CSV format.</p>
        </div>
        <button
          onClick={() => navigate("/cpt")}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border rounded hover:bg-gray-100"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to CPT
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="lg:w-2/5 flex-shrink-0">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-start justify-between mb-4 gap-3">
              <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <FontAwesomeIcon icon={faFileAlt} className="text-blue-600" />
                CSV File Format
              </h3>
              <button
                onClick={handleDownloadTemplate}
                className="flex items-center gap-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg whitespace-nowrap transition-colors"
              >
                <FontAwesomeIcon icon={faDownload} />
                Download Template
              </button>
            </div>

            <div className="space-y-2 mb-5">
              {CSV_COLUMNS.map((col) => (
                <div
                  key={col.label}
                  className="flex items-start gap-2 p-2.5 rounded-lg border bg-red-50 border-red-200"
                >
                  <div className="flex-shrink-0 mt-1.5">
                    <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-semibold text-red-700">{col.label}</span>
                    {col.required && (
                      <span className="ml-1.5 text-xs font-bold text-red-600 uppercase tracking-wide">
                        *required
                      </span>
                    )}
                    <p className="text-xs text-gray-500 mt-0.5">{col.note}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-xs font-bold text-yellow-800 mb-1.5 flex items-center gap-1">
                <FontAwesomeIcon icon={faExclamationTriangle} /> Notes
              </p>
              <ul className="text-xs text-yellow-800 space-y-1 list-disc list-inside">
                <li>CSV header must be cpt_code,cpt_desc,default_price</li>
                <li>Duplicate CPT codes are written to failed rows</li>
                <li>Successful rows are inserted directly into cpt_master</li>
                <li>Maximum file size: 10 MB</li>
                <li>Processing is async. Monitor status in Upload History</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="lg:flex-1">
          <div className="bg-white rounded-lg shadow-md p-6 mb-5">
            <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
              <FontAwesomeIcon icon={faCloudUploadAlt} className="text-blue-600" />
              Upload CSV File
            </h3>

            <div
              className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${
                isDragOver
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-300 hover:border-blue-400 hover:bg-gray-50"
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <FontAwesomeIcon
                icon={faCloudUploadAlt}
                className={`text-5xl mb-3 ${isDragOver ? "text-blue-600" : "text-blue-400"}`}
              />
              <h4 className="text-base font-semibold text-gray-700 mb-1">
                Drag and drop your CSV file here
              </h4>
              <p className="text-sm text-gray-500 mb-3">or click to browse</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                onChange={handleFileSelect}
                className="hidden"
              />
              <p className="text-xs text-gray-400">Accepted: .csv | Max size: 10 MB</p>
            </div>

            {file && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-800">{file.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{(file.size / 1024).toFixed(2)} KB</p>
                </div>
                <button
                  onClick={() => {
                    setFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  className="text-gray-400 hover:text-red-500 transition-colors"
                  title="Remove file"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            )}

            {file && (
              <div className="mt-4 flex gap-3">
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <FontAwesomeIcon icon={faUpload} className={uploading ? "animate-spin" : ""} />
                  {uploading ? "Uploading..." : "Upload & Process"}
                </button>
                <button
                  onClick={() => {
                    setFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  disabled={uploading}
                  className="px-4 py-2.5 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-colors flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faTimes} />
                  Clear
                </button>
              </div>
            )}
          </div>

          {uploadResult && (
            <div
              className={`rounded-lg p-5 ${
                uploadResult.success
                  ? "bg-green-50 border border-green-200"
                  : "bg-red-50 border border-red-200"
              }`}
            >
              <div className="flex items-start gap-3">
                <FontAwesomeIcon
                  icon={uploadResult.success ? faCheckCircle : faTimesCircle}
                  className={`text-2xl flex-shrink-0 mt-0.5 ${
                    uploadResult.success ? "text-green-600" : "text-red-600"
                  }`}
                />
                <div className="flex-1">
                  <h4
                    className={`text-base font-bold mb-1 ${
                      uploadResult.success ? "text-green-800" : "text-red-800"
                    }`}
                  >
                    {uploadResult.success ? "Upload Successful" : "Upload Failed"}
                  </h4>
                  <p className={`${uploadResult.success ? "text-green-700" : "text-red-700"} text-sm`}>
                    {typeof uploadResult.message === "string"
                      ? uploadResult.message
                      : "An error occurred."}
                  </p>

                  {uploadResult.success && uploadResult.uploadId && (
                    <>
                      <div className="mt-3 p-2.5 bg-white rounded border border-green-200">
                        <p className="text-xs text-gray-500 mb-0.5">Upload ID:</p>
                        <p className="font-mono text-xs text-gray-800 break-all">
                          {uploadResult.uploadId}
                        </p>
                        <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                          <FontAwesomeIcon icon={faInfoCircle} />
                          Processing runs in the background.
                        </p>
                      </div>

                      <div className="mt-4 flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={() => navigate(`/processed-cpt-batch-upload/${uploadResult.uploadId}`)}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          <FontAwesomeIcon icon={faEye} />
                          View Processed
                        </button>
                        <button
                          onClick={() => navigate("/history-cpt-batch-upload")}
                          className="flex-1 bg-gray-700 hover:bg-gray-800 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          <FontAwesomeIcon icon={faHistory} />
                          View History
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default CptBatchUploadPage;
